'use client';

import { useState, useRef, useEffect, useCallback, FormEvent, KeyboardEvent, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AnimatedBackground from '@/components/genai-mil/animated-background';
import { GENAI_MIL_MODELS, GenAIModelId, DEFAULT_GENAI_MODEL } from '@/lib/genai-mil/models';
import { GENAI_MIL_SCENARIOS } from '@/lib/genai-mil/scenarios';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mdComponents: Record<string, React.ComponentType<any>> = {
  h1: ({ children }) => <h1 className="text-base font-semibold text-blue-100 mt-3 mb-1 tracking-tight">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-semibold text-blue-200 mt-3 mb-1 uppercase tracking-wider">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-white/90 mt-2 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-white/80 leading-relaxed my-1.5">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic text-white/85">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5 text-white/80">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5 text-white/80">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  hr: () => <hr className="border-white/10 my-3" />,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-blue-400/40 pl-3 text-white/70 italic my-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">{children}</a>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  code: ({ node, inline, className, children, ...props }: any) => {
    if (inline) {
      return <code className="bg-white/10 text-blue-100 px-1 py-0.5 rounded text-[12px] font-mono" {...props}>{children}</code>;
    }
    return <code className="block bg-black/40 border border-white/10 rounded p-3 my-2 overflow-x-auto text-xs font-mono text-white/80" {...props}>{children}</code>;
  },
  pre: ({ children }) => <pre className="bg-black/40 border border-white/10 rounded p-3 my-2 overflow-x-auto text-xs">{children}</pre>,
  table: ({ children }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full">{children}</table></div>,
  th: ({ children }) => <th className="text-left font-semibold border-b border-white/10 px-2 py-1 text-white/90">{children}</th>,
  td: ({ children }) => <td className="border-b border-white/5 px-2 py-1 text-white/75">{children}</td>,
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

// ── Inner client component (needs useSearchParams) ─────────────────────────

function ChatInner() {
  const searchParams = useSearchParams();
  const [selectedModel, setSelectedModel] = useState<GenAIModelId>(DEFAULT_GENAI_MODEL);
  const [selectedScenarioId, setSelectedScenarioId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const models = Object.values(GENAI_MIL_MODELS);

  // Prefill from ?scenario= query param
  useEffect(() => {
    const scenarioId = searchParams.get('scenario');
    if (!scenarioId) return;
    const scenario = GENAI_MIL_SCENARIOS.find((s) => s.id === scenarioId);
    if (scenario) {
      setInput(scenario.body);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const MAX_TEXTAREA_PX = 226;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_PX)}px`;
  }, [input]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: trimmed };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setIsStreaming(true);

    const assistantId = (Date.now() + 1).toString();
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

    try {
      const res = await fetch('/api/genai-mil/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: selectedModel,
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'delta' && event.text) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + event.text } : m))
              );
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch {
            // ignore parse errors on individual lines
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      setMessages((prev) => prev.filter((m) => m.id !== assistantId || m.content !== ''));
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages, selectedModel]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    textareaRef.current?.focus();
  };

  const handleScenarioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) return;
    const scenario = GENAI_MIL_SCENARIOS.find((s) => s.id === id);
    if (scenario) {
      setInput(scenario.body);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
    setSelectedScenarioId('');
  };

  return (
    <div className="relative z-10 min-h-screen text-white flex flex-col" style={{ maxHeight: '100dvh' }}>
      {/* Header */}
      <header className="border-b border-white/10 px-4 py-3 flex items-center gap-3 shrink-0">
        <Link
          href="/genai-mil"
          className="flex items-center gap-1 text-white/40 hover:text-white/70 text-xs transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </Link>
        <div className="w-px h-4 bg-white/10" />
        <div className="flex items-center gap-2 flex-1">
          <div className="text-sm font-semibold tracking-widest text-blue-200">GENAI.MIL</div>
          <span
            className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded hidden sm:inline"
            style={{
              background: 'rgba(180,100,0,0.3)',
              color: '#ffd166',
              border: '1px solid rgba(255,176,0,0.35)',
              fontFamily: 'monospace',
            }}
          >
            TRAINING SIM
          </span>
          <div className="text-[10px] text-white/30 tracking-wider uppercase hidden sm:block">
            Chat · IL5 · CUI (Simulated)
          </div>
        </div>

        {/* Model selector + Scenarios */}
        <div className="flex items-center gap-2">
          <select
            value={selectedScenarioId}
            onChange={handleScenarioChange}
            disabled={isStreaming}
            className="text-xs rounded px-2 py-1.5 text-white/80 bg-transparent border border-white/15 focus:outline-none focus:border-blue-400/50 disabled:opacity-50"
            style={{ background: 'rgba(10,20,50,0.7)' }}
          >
            <option value="" disabled style={{ background: '#0a1430' }}>Scenarios…</option>
            {GENAI_MIL_SCENARIOS.map((s) => (
              <option key={s.id} value={s.id} style={{ background: '#0a1430' }}>
                {s.title}
              </option>
            ))}
          </select>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value as GenAIModelId)}
            disabled={isStreaming}
            className="text-xs rounded px-2 py-1.5 text-white/80 bg-transparent border border-white/15 focus:outline-none focus:border-blue-400/50 disabled:opacity-50"
            style={{ background: 'rgba(10,20,50,0.7)' }}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id} disabled={!m.enabled} style={{ background: '#0a1430' }}>
                {m.label}{!m.enabled ? ' (removed)' : ''}
              </option>
            ))}
          </select>
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              className="text-[10px] text-white/30 hover:text-white/60 tracking-wider uppercase transition-colors px-2 py-1"
            >
              Clear
            </button>
          )}
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-20">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-4xl font-bold text-white/10 tracking-tight">GenAI.mil</div>
              <span
                className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded self-center"
                style={{
                  background: 'rgba(180,100,0,0.2)',
                  color: 'rgba(255,209,102,0.5)',
                  border: '1px solid rgba(255,176,0,0.2)',
                  fontFamily: 'monospace',
                }}
              >
                TRAINING SIM
              </span>
            </div>
            <div className="text-sm text-white/30 max-w-sm leading-relaxed">
              Training environment — simulates the GenAI.mil platform. Use BLUF format for faster responses.
            </div>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {[
                'Draft a mission briefing summary',
                'Summarize a DoD policy memo',
                'Help with OPORD format',
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-xs px-3 py-1.5 rounded border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div
                className="w-7 h-7 rounded shrink-0 flex items-center justify-center text-[10px] font-bold text-blue-200 mt-0.5"
                style={{ background: 'rgba(30,80,200,0.3)', border: '1px solid rgba(100,160,255,0.2)' }}
              >
                AI
              </div>
            )}
            <div
              className={`rounded px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'max-w-[80%] whitespace-pre-wrap text-white/90'
                  : 'max-w-[85%] text-white/80'
              }`}
              style={
                msg.role === 'user'
                  ? { background: 'rgba(30,80,200,0.25)', border: '1px solid rgba(100,160,255,0.2)' }
                  : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }
              }
            >
              {msg.role === 'assistant' ? (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {msg.content}
                  </ReactMarkdown>
                  {msg.content === '' && isStreaming && (
                    <span className="inline-block w-2 h-4 bg-blue-400/60 animate-pulse align-middle ml-0.5" />
                  )}
                </>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {error && (
          <div
            className="mx-auto max-w-lg text-xs px-4 py-3 rounded text-red-300"
            style={{ background: 'rgba(200,30,30,0.15)', border: '1px solid rgba(200,30,30,0.3)' }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-white/10 px-4 py-3">
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
            rows={1}
            disabled={isStreaming}
            className="flex-1 resize-none rounded px-3 py-2 text-sm text-white/90 placeholder-white/25 bg-transparent border border-white/15 focus:outline-none focus:border-blue-400/50 disabled:opacity-50 min-h-[40px] max-h-[226px] overflow-y-auto"
            style={{ background: 'rgba(10,20,50,0.6)', lineHeight: '1.5' }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isStreaming}
            className="px-4 py-2 rounded text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #1a4fd6 0%, #0d2d7a 100%)',
              border: '1px solid rgba(100,160,255,0.35)',
              minWidth: '72px',
            }}
          >
            {isStreaming ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce [animation-delay:0.1s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce [animation-delay:0.2s]" />
              </span>
            ) : (
              'Send'
            )}
          </button>
        </form>
        <div className="mt-1.5 text-[10px] text-white/20 text-center">
          TRAINING ENVIRONMENT · Not an official U.S. Government system · Do not enter real classified information · GenAI.mil Training Simulator
        </div>
      </div>
    </div>
  );
}

// ── Page wrapper with Suspense (required by useSearchParams) ──────────────

export default function GenAIMilChatPage() {
  return (
    <>
      <AnimatedBackground />
      <Suspense
        fallback={
          <div className="relative z-10 min-h-screen flex items-center justify-center">
            <div className="text-white/30 text-sm">Loading…</div>
          </div>
        }
      >
        <ChatInner />
      </Suspense>
    </>
  );
}
